if (!customElements.get('pdp-service')) {
  class PDPService extends HTMLElement {
    connectedCallback() {
      this.videoTarget = this.querySelector('[data-pdp-service-video-target]');
      this.iframe = this.videoTarget?.querySelector('iframe');
      this.triggers = [...this.querySelectorAll('[data-pdp-service-video-trigger]')];
      this.media = this.querySelector('.pdp-service__media');
      this.activeTrigger = null;

      this.onTriggerClick = (event) => this.handleTriggerClick(event);
      this.onKeyDown = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-video-playing')) {
          this.stopVideo();
        }
      };

      for (const trigger of this.triggers) {
        trigger.setAttribute('aria-pressed', 'false');
        trigger.addEventListener('click', this.onTriggerClick);
      }

      document.addEventListener('keydown', this.onKeyDown);
    }

    disconnectedCallback() {
      for (const trigger of this.triggers || []) {
        trigger.removeEventListener('click', this.onTriggerClick);
      }

      document.removeEventListener('keydown', this.onKeyDown);
      this.stopVideo({ restoreFocus: false });
    }

    handleTriggerClick(event) {
      const trigger = event.currentTarget;
      const embedSrc = this.getEmbedSrc(trigger.dataset.videoUrl || '');

      if (!embedSrc || !this.videoTarget || !this.iframe) return;

      if (this.activeTrigger === trigger && this.classList.contains('is-video-playing')) {
        this.stopVideo();
        return;
      }

      this.playVideo(embedSrc, trigger);
    }

    playVideo(embedSrc, trigger) {
      this.stopVideo({ restoreFocus: false });

      this.activeTrigger = trigger;
      this.activeTrigger.setAttribute('aria-pressed', 'true');
      this.iframe.src = embedSrc;
      this.videoTarget.hidden = false;
      this.videoTarget.setAttribute('aria-hidden', 'false');
      this.classList.add('is-video-playing');

      if (window.matchMedia('(max-width: 1024px)').matches) {
        this.media?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }

    stopVideo({ restoreFocus = true } = {}) {
      if (!this.videoTarget || !this.iframe) return;

      this.iframe.src = 'about:blank';
      this.videoTarget.hidden = true;
      this.videoTarget.setAttribute('aria-hidden', 'true');
      this.classList.remove('is-video-playing');

      if (this.activeTrigger) {
        this.activeTrigger.setAttribute('aria-pressed', 'false');
        if (restoreFocus) this.activeTrigger.focus();
        this.activeTrigger = null;
      }
    }

    getEmbedSrc(rawUrl) {
      let url;

      try {
        url = new URL(rawUrl);
      } catch {
        return '';
      }

      if (url.protocol !== 'https:' && url.protocol !== 'http:') return '';

      const hostname = url.hostname.replace(/^www\./, '');

      // enablejsapi/origin: lets assets/datalayer-tracking.js attach a
      // YT.Player for video_played / video_completed dataLayer events.
      const ytParams = `autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;

      if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') {
        const id = url.searchParams.get('v') || url.pathname.match(/\/(?:embed|shorts)\/([^/?]+)/)?.[1];
        return id ? `https://www.youtube.com/embed/${id}?${ytParams}` : '';
      }

      if (hostname === 'youtu.be') {
        const id = url.pathname.slice(1).split('/')[0];
        return id ? `https://www.youtube.com/embed/${id}?${ytParams}` : '';
      }

      if (hostname === 'vimeo.com' || hostname === 'player.vimeo.com') {
        const id = url.pathname.match(/\/(?:video\/)?(\d+)/)?.[1];
        return id ? `https://player.vimeo.com/video/${id}?autoplay=1` : '';
      }

      return '';
    }
  }

  customElements.define('pdp-service', PDPService);
}
